//
// Copyright (c) 2011-2020 by Curalate, Inc.
//
import throttle from 'lodash/fp/throttle';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  Grid as VirtualizedGrid,
  WindowScroller,
  WindowScrollerChildProps,
} from 'react-virtualized';
import styled from 'styled-components';

import VirtualPositionTracker, {
  IChangedVisibilityType,
} from '@components/VirtualPositionTracker';
import { CenteredSpinner } from '@components/atoms/spinner/Spinner';
import { MetatagText } from '@components/atoms/text/Text';
import { Grid } from '@components/organisms/content-card-grid/ContentCardGrid';
import {
  ContentCardRangeSelectContext,
  IRangeSelectConfig,
  IRangeSelectedItem,
} from '@components/organisms/content-card-grid/ContentCardRangeSelectContext';
import { useResizeObserver } from '@hooks/useResizeObserver';
import PartialCuid from '@models/PartialCuid';
import { isDefined } from '@utils/filterPredicates';

interface Props extends React.ComponentPropsWithoutRef<'div'> {
  cardHeight: number;
  cardWidth: number;
  gridWidth?: number;
  scrollContainerRef?: React.RefObject<HTMLElement>;
  isRefreshingContent: boolean;
  postFixConfig?: IPostFixConfigProps;
  inlineLoadingConfig?: IInlineLoadingConfigProps;
  numRowsFromEndBeforeCallback?: number;
  // TODO: In the future, make this selector string unnecessary. Relying on selectors is
  // flaky, but is necessary as long as we continue to use angular and continue to have
  // inconsistent scrollable containers throughout the app.
  scrollContainerSelector?: string;
  includeCardPadding?: boolean;
  orderedPartialCuids: PartialCuid[];
  onTopReached?: () => void;
  onBottomReached?: () => void;
  onItemsVisibilityChange?: (data: IChangedVisibilityType) => void;
}

interface IPostFixConfigProps {
  shouldShowPostFixComponent: boolean;
  postFixComponent: React.ReactNode;
}

interface IInlineLoadingConfigProps {
  shouldShowInlineLoadingComponent: boolean;
  inlineLoadingCount: number;
  inlineLoadingComponent: React.ReactNode;
}

interface IOverscanIndicesGetterParams {
  direction: 'horizontal' | 'vertical';
  scrollDirection: 1 | -1;
  overscanCellsCount: number;
  startIndex: number;
  stopIndex: number;
  cellCount: number;
}

interface ICellRendererParams {
  columnIndex: number;
  rowIndex: number;
  style: CSSProperties;
  isScrolling: boolean;
  key: string;
}

interface IAxisRenderer {
  index: number;
}

interface IOverscanIndices {
  overscanStartIndex: number;
  overscanStopIndex: number;
}

// We need to store this padding in a value because
// we cannot rely on css mixins. The virtual scrolling
// library we use will not work with traditional css styles
// that impact dimensions or spacing.
export const CONTENT_CARD_PADDING = 16;

/**
 * The ContentCardGrid organism displays its children prop
 * in a grid with standard spacing between each child node. It
 * is built to house a grid of "cards" which are, broadly speaking,
 * uniformly sized boxes of content that use our sheet styling.
 *
 * @param cardHeight The height of the ContentCards used. This must match the height of the cards
 * as they are styled. See below for an example
 * @param cardWidth See cardHeight
 * @param gridWidth The width of the grid in pixels. If not specified, will use the scrollContainerRef to
 * calculate width
 * @param scrollContainerRef A ref connected to the grid's container. If defined, can be used to automatically
 * calculate the width of the grid based on the width of the container.
 * @param isRefreshingContent True if the content in the grid is being refreshed. This should include the
 * initial request for content as it is a transition from 0 => N items in the grid. This is used to
 * reset the scroll position
 * @param includeCardPadding If true, will simulate 16px of padding between all cards in the grid
 * @param numRowsFromEndBeforeCallback The number of rows from the bottom of the grid that, when scrolled to, should
 * result in the onBottomReached prop being executed.
 * @param scrollContainerSelector If defined, will be used to select which element should control the scroll possition
 * of the grid. The grid will only respond to scroll events that occur in this element. `window` by default.
 * @param postFixConfig A config to determine which component to render at the end of the grid. Good for displaying a message or a component indicating that the user has reached the end of the content available in the grid.
 * @param inlineLoadingConfig A config to determine which component to render inline with the content when the grid is loading. Good for showing a loading indicator or placeholders during content loading.
 * @param onTopReached Called when the user scrolls to the top of the grid. Automatically throttled at 250ms
 * @param onBottomReached Called when the user scrolls to the bottom of the grid. Automatically throttled at 250ms
 * @param onItemsVisibilityChange Called whenever any item's visibility is changed
 *
 * @example Setting the cardHeight and cardWidth props for statically sized cards
 * <VirtualizedContentCardGrid
 *   cardHeight={ 150 }
 *   cardWidth={ 300 }
 *   gridWidth={ 800 }
 *   isRefreshingContent={ isRefreshingContent }
 *   postfixComponent={ <div>{ loadedAllContent ? 'No More Content' : 'Loading...'  }</div> }
 *   scrollContainerSelector='.the-scrollable-parent-classname'
 *   onBottomReached={ loadMore() }
 * >
 *   { items.map((item) => (
 *     <StyledMediaCard />
 *   )) }
 * </VirtualizedContentCardGrid>
 *
 * const StyledMediaCard styled(MediaCard)`
 *   height: 150px;
 *   width: 300px;
 * `
 */
export const VirtualizedContentCardGrid = ({
  className,
  cardHeight,
  cardWidth,
  gridWidth,
  scrollContainerRef,
  isRefreshingContent,
  postFixConfig,
  inlineLoadingConfig,
  scrollContainerSelector,
  numRowsFromEndBeforeCallback = 2,
  includeCardPadding = true,
  onTopReached = () => null,
  onBottomReached = () => null,
  onItemsVisibilityChange = () => null,
  orderedPartialCuids,
  children,
}: Props) => {
  const [lastSelectedItem, setLastSelectedItem] =
    useState<IRangeSelectedItem | null>(null);

  const rangeSelectContextValue: IRangeSelectConfig = {
    orderedPartialCuids,
    lastSelectedItem,
    setLastSelectedItem,
  };

  const gridRef = useRef<VirtualizedGrid>();
  const windowScrollerRef = useRef<WindowScroller>(null);
  // If need be, the 250ms throttle below can be made configurable via a prop
  const onBottomReachedThrottled = useRef<() => void>(
    throttle(250, onBottomReached),
  ).current;
  const onTopReachedThrottled = useRef<() => void>(
    throttle(250, onTopReached),
  ).current;
  const { width: containerWidth } = useResizeObserver(scrollContainerRef);
  const { shouldShowPostFixComponent, postFixComponent } = postFixConfig ?? {};
  const {
    shouldShowInlineLoadingComponent,
    inlineLoadingCount,
    inlineLoadingComponent,
  } = inlineLoadingConfig ?? {};

  // The total grid width will use the gridWidth prop if specified. Otherwise it will use the
  // containerWidth.
  const totalGridWidth = gridWidth ?? containerWidth;

  // Reset the scroll position whenever content is being refreshed (e.g. after changing
  // filters or running a query).
  useEffect(() => {
    if (isRefreshingContent) {
      gridRef.current?.scrollToPosition({ scrollLeft: 0, scrollTop: 0 });
    }
  }, [isRefreshingContent]);

  // The scrollable element that contains the virtualized component. Only ever
  // select it once to avoid hammering the DOM
  const getScrollElement = useCallback(
    () =>
      scrollContainerSelector
        ? document.querySelectorAll(scrollContainerSelector)[0]
        : window,
    [scrollContainerSelector],
  );

  const childrenArray = React.Children.toArray(children);

  // The content card height and width must include padding in order to achieve spacing
  // between each card
  const totalCardWidth =
    cardWidth + (includeCardPadding ? CONTENT_CARD_PADDING : 0);
  const totalCardHeight =
    cardHeight + (includeCardPadding ? CONTENT_CARD_PADDING : 0);
  const totalColumnCount = totalGridWidth
    ? Math.max(Math.floor(totalGridWidth / totalCardWidth), 1)
    : 0;
  const totalRowCount = totalColumnCount
    ? Math.ceil(childrenArray.length / totalColumnCount)
    : 0;

  const getRowHeight: (params: IAxisRenderer) => number = ({ index }) => {
    // Add additional space on the first row to ensure that we have a uniform 16px
    // of padding around the grid. This also ensures that cards and their drop
    // shadows don't get clipped.
    if (index === 0) {
      return totalCardHeight + CONTENT_CARD_PADDING;
    } else {
      return totalCardHeight;
    }
  };

  const getColumnWidth: (params: IAxisRenderer) => number = ({ index }) => {
    // Add additional space on the first column to ensure that we have a uniform 16px
    // of padding around the grid. This also ensures that cards and their drop
    // shadows don't get clipped.
    if (index === 0) {
      return totalCardWidth + CONTENT_CARD_PADDING;
    } else {
      return totalCardWidth;
    }
  };

  const getItem = (rowIndex: number, columnIndex: number) => {
    const itemIndex = totalColumnCount * rowIndex + columnIndex;

    if (isDefined(childrenArray[itemIndex])) {
      return childrenArray[itemIndex];
    }

    if (shouldShowInlineLoadingComponent) {
      return inlineLoadingComponent;
    }

    return undefined;
  };

  const cellRenderer: (params: ICellRendererParams) => React.ReactElement = ({
    columnIndex,
    rowIndex,
    style,
    key,
  }) => {
    return (
      <GridItem
        isFirstColumn={columnIndex === 0}
        isFirstRow={rowIndex === 0}
        style={style}
        key={key}
      >
        {getItem(rowIndex, columnIndex)}
      </GridItem>
    );
  };

  const overscanIndicesGetter: (
    params: IOverscanIndicesGetterParams,
  ) => IOverscanIndices = ({
    direction,
    cellCount,
    overscanCellsCount,
    startIndex,
    stopIndex,
  }) => {
    if (direction === 'vertical') {
      return {
        overscanStartIndex: Math.max(0, startIndex - overscanCellsCount),
        overscanStopIndex: Math.min(
          cellCount - 1,
          stopIndex + overscanCellsCount,
        ),
      };
    } else {
      // Always render all visible columns because our grids do
      // not scroll horizontally
      return {
        overscanStartIndex: startIndex,
        overscanStopIndex: stopIndex,
      };
    }
  };

  return (
    <GridContainer className={className}>
      <WindowScroller
        ref={windowScrollerRef}
        scrollElement={getScrollElement()}
      >
        {(windowScrollerRenderProps: WindowScrollerChildProps) => {
          return (
            <VirtualPositionTracker
              items={childrenArray}
              onTopReached={onTopReachedThrottled}
              onEndReached={onBottomReachedThrottled}
              onItemsVisibilityChange={onItemsVisibilityChange}
              numRowsFromBottom={numRowsFromEndBeforeCallback}
            >
              {({ onItemsRendered }) => (
                <div>
                  <div ref={windowScrollerRenderProps.registerChild}>
                    <ContentCardRangeSelectContext.Provider
                      value={rangeSelectContextValue}
                    >
                      <StyledVirtualizedGrid
                        autoHeight={true}
                        autoWidth={true}
                        ref={gridRef}
                        scrollTop={windowScrollerRenderProps.scrollTop}
                        height={windowScrollerRenderProps.height}
                        rowCount={totalRowCount}
                        columnCount={totalColumnCount}
                        columnWidth={getColumnWidth}
                        rowHeight={getRowHeight}
                        cellRenderer={cellRenderer}
                        onSectionRendered={onItemsRendered}
                        width={totalGridWidth}
                        overscanIndicesGetter={overscanIndicesGetter}
                      />
                    </ContentCardRangeSelectContext.Provider>
                  </div>
                  {shouldShowInlineLoadingComponent && (
                    <Grid className={className} itemWidth={`${cardWidth}px`}>
                      {new Array(inlineLoadingCount)
                        .fill(0)
                        .map((_slot, idx) => (
                          <StyledInlineLoadingComponentWrapper
                            key={idx}
                            includeCardPadding={includeCardPadding}
                          >
                            {inlineLoadingComponent}
                          </StyledInlineLoadingComponentWrapper>
                        ))}
                    </Grid>
                  )}
                  {shouldShowPostFixComponent && postFixComponent}
                </div>
              )}
            </VirtualPositionTracker>
          );
        }}
      </WindowScroller>
    </GridContainer>
  );
};

// We have to set the will-change style to its inherited value / default. The
// Grid component created by the react-virtualized library automatically
// includes a `will-change: transform` style. The `transform` style is never
// actually used by the library, and there's no practical performance improvements
// gained by that style. Normally, we'd just leave the style as-is and ignore it, but
// because our popovers rely on absolute/fixed positioning, we cannot have a container
// element that uses the transform property because that changes the local coordinate
// system, which means that fixed position elements are fixed relative to their ancestor
// rather than the viewport.
const StyledVirtualizedGrid = styled(VirtualizedGrid)`
  display: flex;
  justify-content: center;
  /* Without this, popovers are broken, but we take a perf hit */
  will-change: unset !important;
  /* Prevent the tabindex outline from appearing */
  outline: none !important;
`;

const GridContainer = styled.div``;

// Used to wrap each grid item. The elements that VariableSizeGrid creates
// include additional height and width to simulate padding between each card.
// If this is a card for the first row or first column, center the content
// because we've added additional height to the container to account for drop
// shadows and bulk selection resize transformations.
const GridItem = styled.div<{ isFirstRow: boolean; isFirstColumn: boolean }>(
  ({ isFirstRow, isFirstColumn }) => `
  display: flex;
  justify-content: ${isFirstColumn ? 'center' : 'flex-start'};
  align-items: ${isFirstRow ? 'center' : 'flex-start'};
`,
);

const StyledInlineLoadingComponentWrapper = styled.div<{
  includeCardPadding: boolean;
}>(
  ({ includeCardPadding }) => `
  padding: 0 ${includeCardPadding ? CONTENT_CARD_PADDING : 0}px;
`,
);

interface EndOfContentProps {
  endOfContentText?: string;
}

const EndOfContent = (
  {
    endOfContentText
  }: EndOfContentProps
) => {
  return (
    <Wrapper>
      <Text>{endOfContentText ?? 'No More Media'}</Text>
    </Wrapper>
  );
};

const LoadingSpinner = () => {
  return (
    <Wrapper>
      <CenteredSpinner />
    </Wrapper>
  );
};

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
  min-height: 120px;
`;

const Text = styled(MetatagText)`
  display: flex;
  justify-content: center;
`;

VirtualizedContentCardGrid.EndOfContent = EndOfContent;
VirtualizedContentCardGrid.LoadingSpinner = LoadingSpinner;

export default VirtualizedContentCardGrid;
